import { useCallback, useState } from 'react';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import useModal from 'src/components/Modals/hooks/useModal';
import RadioGroup from 'src/components/primitives/RadioGroup/RadioGroup';
import Button from 'src/components/primitives/Button/Button';
import { usePrivyEmbeddedWallet } from 'src/hooks/wallets/privy/usePrivyEmbeddedWallet';
import { useSubAccountsDelegation } from 'src/hooks/wallets/privy/useSubAccountsDelegation';
import { PublicKey } from '@solana/web3.js';
import { notify } from 'src/utils/notifications';
import Checkbox from 'src/components/primitives/Checkbox/Checkbox';
import { cm } from 'src/utils/classNames';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { BaseRadioItemProps } from 'src/components/primitives/RadioGroup/types';
import { useWalletsV2 } from 'src/hooks/wallets/useWalletsV2';

type OptionValue = 'hardware-wallet' | 'fund-manager';

const RADIO_OPTIONS: BaseRadioItemProps<OptionValue>[] = [
	{
		id: 'hardware-wallet',
		value: 'hardware-wallet',
		label: (
			<div className="flex flex-col items-start gap-y-xs text-left">
				<span className="typo-t3 text-text-default">
					For Hardware Wallet Users
				</span>
				<span className="typo-b4 text-text-secondary">
					You can now use Auto-Signing for a smoother experience. This will
					update your current delegated account and you won&apos;t need to pay
					gas fees anymore.
				</span>
			</div>
		),
		className: 'items-start gap-x-md',
	},
	{
		id: 'fund-manager',
		value: 'fund-manager',
		label: (
			<div className="flex flex-col items-start gap-y-xs text-left">
				<span className="typo-t3 text-text-default">
					For Users delegating to Fund Managers
				</span>
				<span className="typo-b4 text-text-secondary">
					We recommend NOT enabling Auto-Signing. Doing so will remove your
					delegation, and your fund manager will no longer be able to trade on
					your behalf.
				</span>
			</div>
		),
		className: 'items-start gap-x-md',
	},
];

export default function EnableSignlessTradingForDelegatedModal() {
	const { embeddedWallet } = usePrivyEmbeddedWallet();
	const { authorityWallet } = useWalletsV2();

	const { setupDelegateForSubaccounts } = useSubAccountsDelegation();
	const { hide } = useModal();
	const { captureEvent } = usePostHogCapture();

	const [selectedOption, setSelectedOption] =
		useState<OptionValue>('hardware-wallet');
	const [isUpdatingDelegate, setIsUpdatingDelegate] = useState(false);
	const [hasCheckedTermsForFundManager, setHasCheckedTermsForFundManager] =
		useState(false);

	const isConfirmDisabled =
		selectedOption === 'fund-manager' && !hasCheckedTermsForFundManager;

	const updateCurrentAccountDelegate = useCallback(async () => {
		if (!embeddedWallet) return;

		let success = false;

		try {
			setIsUpdatingDelegate(true);

			await setupDelegateForSubaccounts(
				new PublicKey(embeddedWallet.address),
				true,
				authorityWallet ?? undefined
			);

			notify({
				type: 'success',
				message: 'Auto-Signing enabled for your account.',
			});

			hide();
			success = true;
		} catch (error) {
			console.error(error);
			notify({
				type: 'error',
				message: 'Failed to enable Auto-Signing',
			});
		} finally {
			captureEvent('enable_signless_trading_for_delegated_modal_confirm', {
				success,
				delegated_selected_option: selectedOption,
			});
			setIsUpdatingDelegate(false);
		}
	}, [
		authorityWallet,
		embeddedWallet,
		selectedOption,
		setupDelegateForSubaccounts,
		captureEvent,
		hide,
	]);

	return (
		<ResponsiveModal.Container onClose={hide}>
			<ResponsiveModal.Title>Enable Auto-Signing</ResponsiveModal.Title>

			<ResponsiveModal.Body className="py-2xl">
				<RadioGroup.Default
					size="md"
					orientation="vertical"
					items={RADIO_OPTIONS}
					value={selectedOption}
					onValueChange={setSelectedOption}
					className="gap-0 divide-y divide-stroke-divider [&>div]:py-2xl [&>div]:first:pt-0 [&>div]:last:pb-0"
				/>
			</ResponsiveModal.Body>

			<ResponsiveModal.Footer className="flex-col">
				<div
					className={cm(
						'overflow-hidden transition-all duration-300 ease-in-out',
						selectedOption === 'fund-manager'
							? 'max-h-[100px] opacity-100'
							: 'max-h-0 opacity-0'
					)}
				>
					<Checkbox.Default
						id="enable-signless-trading-for-delegated-fund-manager-modal-terms"
						label="I understand that proceeding with Auto-Signing will remove the fund manager access"
						checked={hasCheckedTermsForFundManager}
						onCheckedChange={() =>
							setHasCheckedTermsForFundManager((prev) => !prev)
						}
						labelClassName="whitespace-normal min-w-0"
					/>
				</div>

				<Button
					size="lg"
					className="w-full"
					isLoading={isUpdatingDelegate}
					onClick={updateCurrentAccountDelegate}
					disabled={isConfirmDisabled}
				>
					Confirm
				</Button>
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
}
